<script lang="ts">
	import { formatBigWithLocale } from '../../helper/Parser';
	import Icon from '../Icon.svelte';
	import { handleStoreError } from '../../stores/GeneralStore';
	import { clickOutside } from '../../helper/Extensions';

	interface Props {
		id?: string | null;
		hint?: string;
		selected?: any | null;
		options?: any[];
		displayPropertyName?: string;
		//Property used to display another property only if showReduced is set.
		reducedDisplayName?: string | null;
		callFunction?: ((object: any) => Promise<any>) | null;
		patchPropertyName: string;
		showAsNumber?: boolean;
		showReduced?: boolean;
		selectionPropertyName: string;
		positionStyle?: boolean;
		tabSwitcher?: boolean;
		disabled?: boolean;
		wrappInOptionalParameter?: boolean;
		dropDirection?: 'bottom' | 'top';
		selection?: import('svelte').Snippet<[any]>;
		content?: import('svelte').Snippet<[any]>;
		onOptionSelected?: (item: any) => void;
	}

	let {
		id = null,
		hint = '',
		selected = $bindable(null),
		options = [],
		displayPropertyName = 'name',
		reducedDisplayName = null,
		callFunction = null,
		patchPropertyName,
		showAsNumber = false,
		showReduced = false,
		selectionPropertyName,
		positionStyle = false,
		tabSwitcher = false,
		disabled = false,
		wrappInOptionalParameter = false,
		dropDirection = 'bottom',
		selection,
		content,
		onOptionSelected
	}: Props = $props();

	let showDropdown = $state(false);

	function toggleDropdown() {
		showDropdown = !showDropdown;
	}

	function handleSelect(option: any) {
		selected = option;
		if (callFunction) {
			handlePatch();
		} else {
			onOptionSelected?.(option);
		}
	}

	async function handlePatch() {
		//We first try to patch the value
		try {
			const dynamicObject: any = {};

			//Check if we should wrap the property in an optional
			if (wrappInOptionalParameter) {
				dynamicObject[patchPropertyName] = {
					value: selected[selectionPropertyName]
				};
			} else {
				dynamicObject[patchPropertyName] = selected[selectionPropertyName];
			}

			await callFunction!(dynamicObject);
		} catch (error) {
			//Check if we have an error type
			handleStoreError('general.patch_failed', error);
		}
	}

	function getSelectedDisplayValue(object: any, showReduced: boolean): string {
		//Decide if we need to format is as a number
		if (showAsNumber) {
			return formatBigWithLocale(
				object[displayPropertyName] != null ? object[displayPropertyName] : ''
			);
		} else {
			const value = object[displayPropertyName] || '';

			//We have a string value, show it reduced when requestest.
			if (showReduced && reducedDisplayName) {
				return object[reducedDisplayName] || '';
			} else if (showReduced) {
				return value.substring(0, 1).toUpperCase();
			} else {
				return value;
			}
		}
	}

	function handleKeyDown(event: KeyboardEvent) {
		let handeld = false;

		switch (event.key) {
			case 'Enter': {
				handeld = true;
				showDropdown = !showDropdown;
				break;
			}
			case 'ESC':
			case 'Escape': {
				handeld = true;
				showDropdown = false;
				break;
			}
		}

		if (handeld) {
			event.stopPropagation();
			event.preventDefault();
		}
	}
</script>

<button
	{id}
	class:normal-style={!positionStyle}
	class:position-style={positionStyle}
	class="dropdown outline-none w-full"
	class:h-full={positionStyle}
	class:switcher={tabSwitcher}
	class:disabled
	{disabled}
	tabindex="0"
	use:clickOutside={() => {
		showDropdown = false;
	}}
	onkeydown={handleKeyDown}
	onclick={toggleDropdown}
>
	<div class="relative outline-none w-full h-full">
		<div class="flex items-center p-2">
			{#if selection}
				<div class="flex-grow">
					{@render selection?.({ option: selected })}
				</div>
			{:else}
				<p class="flex-grow text-left truncate">
					{selected === null || selected === undefined
						? hint
						: getSelectedDisplayValue(selected, showReduced)}
				</p>
			{/if}
			<Icon name="chevron-down" size="small" classInfo="dark:fill-white ml-4" />
		</div>

		{#if showDropdown}
			<div
				style="max-height: 200px; min-width: 100%;"
				class="absolute bg-white outline-none border border-gray dark:border-dark-300 rounded shadow-xl z-40 flex flex-col overflow-y-auto"
				class:bottom-0={dropDirection == 'top'}
			>
				{#each options as option}
					<!-- svelte-ignore node_invalid_placement_ssr -->
					<button
						class="
						dark:bg-dark-400
						dark:text-white
                        focus:text-white focus:bg-secondary-200
                        dark:focus:bg-dark-200
                        hover:text-white hover:bg-secondary-200
                        dark:hover:bg-dark-200
                        border-b b-1 p-2 border-gray dark:border-dark-300"
						onclick={(_) => handleSelect(option)}
					>
						{#if content}
							{@render content?.({ option })}
						{:else}
							<p>{getSelectedDisplayValue(option, false)}</p>
						{/if}
					</button>
				{/each}
			</div>
		{/if}
	</div>
</button>

<style>
	.normal-style :global {
		--tw-border-opacity: 1;
		border-color: rgb(227 227 227 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
}
.normal-style :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(56 56 56 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}
	.normal-style :global {
		border-radius: 0.25rem;
		border-width: 2px;
	}

	.position-style {
		border-radius: 0;
		border-width: 0 2px;
		border-color: #e3e3e3;
	}

	.dropdown:focus :global {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
}

	.dropdown:focus :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
}

	.dropdown:hover:not(.disabled) :global {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
}

	.dropdown:hover:not(.disabled) :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
}

	.disabled :global {
		--tw-bg-opacity: 1;
		background-color: rgb(242 242 242 / var(--tw-bg-opacity, 1));
}

	.disabled :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(36 36 36 / var(--tw-bg-opacity, 1));
}
</style>
